class ImpactContentMedia extends HTMLElement {
    static get observedAttributes() {
        return [
            'autoplay',
            'interval',
            'bg-images',
            'next-btn',
            'prev-btn',
            'dots-container',
            'initial-slide', // lowercase in DOM
        ];
    }

    constructor() {
        super();
        // Core state
        this._track = null;
        this._slides = [];
        this._index = 0;
        this._animating = false;
        this._timer = null;
        this._fadeMs = 400;
        this._bgFadeMs = 300;
        this._heightMs = 400;
        this._paused = false;

        // Video-autoplay awareness
        this._videoHold = false;              // true while any active video is playing
        this._playingVideosCount = 0;         // number of videos currently playing
        this._boundSlideVideoHandlers = [];   // [{el, play, pause, ended}]
        this._boundBgVideoHandlers = [];      // same for bg container

        // DOM refs
        this._ro = null;          // ResizeObserver
        this._bgEl = null;        // background target
        this._nextEl = null;      // external controls
        this._prevEl = null;

        // Dots
        this._dotsWrap = null;
        this._dots = [];
        this._DOT_ACTIVE_CLASS = 'is-active';

        // Bound handlers for controls
        this._onNextClick = (e) => { e.preventDefault(); if (this._animating) return; this._clearTimer(); this.next(); };
        this._onPrevClick = (e) => { e.preventDefault(); if (this._animating) return; this._clearTimer(); this.prev(); };
        // Dots: 1-based indices in markup; convert to 0-based
        this._onDotsClick = (e) => {
            const el = e.target.closest('[data-slide-index]');
            if (!el || !this._dotsWrap?.contains(el)) return;
            e.preventDefault();
            if (this._animating) return;
            const idx = parseInt(el.getAttribute('data-slide-index') || '-1', 10) - 1;
            if (!Number.isFinite(idx) || idx < 0 || idx >= this._slides.length) return;
            this._clearTimer();
            this.goTo(idx, -50);
        };
    }

    connectedCallback() {
        this._track = this.querySelector('.icm--track');
        if (!this._track) { console.warn('[impact-content-media] Missing .icm--track'); return; }

        this._slides = Array.from(this._track.querySelectorAll('.icm--slide'));
        if (!this._slides.length) return;

        // Initial slide index
        const startIndex = Math.min(Math.max(0, this.initialSlide), this._slides.length - 1);
        this._index = startIndex;

        // Init slides
        this._slides.forEach((s, i) => {
            if (i === this._index) {
                s.classList.add('is-active');
                s.style.opacity = '1';
                s.style.position = 'relative';
            } else {
                s.classList.remove('is-active', 'is-entering', 'is-exiting');
                s.style.opacity = '0';
                s.style.position = 'absolute';
            }
        });

        // Lock height to current
        this._setTrackHeight(this._slides[this._index].offsetHeight, true);

        // BG target + initial content
        this._resolveBgTarget();
        this._syncBgFromSlide(this._slides[this._index]).then(() => {
            // Bind video listeners
            this._bindVideosIn(this._bgEl, this._boundBgVideoHandlers);
            this._bindVideosIn(this._slides[this._index], this._boundSlideVideoHandlers);

            // Resize observer
            this._setupResizeObserver();

            // Controls
            this._resolveControls();
            this._bindControls();

            // Dots
            this._resolveDots();
            this._bindDots();
            this._updateActiveDot();

            // Autoplay + hover pause
            if (this.autoplay) {
                this._startAutoplay();
                this.addEventListener('mouseenter', this._pauseAutoplay);
                this.addEventListener('mouseleave', this._resumeAutoplay);
                this.addEventListener('touchstart', this._pauseAutoplay, { passive: true });
                this.addEventListener('touchend', this._resumeAutoplay, { passive: true });
            }
        });
    }

    disconnectedCallback() {
        this._clearTimer();
        this.removeEventListener('mouseenter', this._pauseAutoplay);
        this.removeEventListener('mouseleave', this._resumeAutoplay);
        this.removeEventListener('touchstart', this._pauseAutoplay);
        this.removeEventListener('touchend', this._resumeAutoplay);
        if (this._ro) this._ro.disconnect();

        this._unbindVideos(this._boundSlideVideoHandlers);
        this._unbindVideos(this._boundBgVideoHandlers);

        this._unbindControls();
        this._unbindDots();
    }

    attributeChangedCallback(name) {
        if (name === 'autoplay') { this.autoplay ? this._startAutoplay() : this._clearTimer(); }
        if (name === 'interval') { if (this.autoplay) this._startAutoplay(); }
        if (name === 'bg-images') {
            this._resolveBgTarget();
            const active = this._slides?.[this._index];
            if (active) {
                this._syncBgFromSlide(active).then(() => {
                    // rebind BG video listeners to new target
                    this._bindVideosIn(this._bgEl, this._boundBgVideoHandlers);
                });
            }
        }
        if (name === 'next-btn' || name === 'prev-btn') {
            this._unbindControls(); this._resolveControls(); this._bindControls();
        }
        if (name === 'dots-container') {
            this._unbindDots(); this._resolveDots(); this._bindDots(); this._updateActiveDot();
        }
    }

    // ========== VIDEO-AWARE AUTOPLAY HELPERS ==========
    _pauseAutoplayForVideo() {
        this._videoHold = true;
        this._clearTimer();
    }

    _resumeAutoplayIfAllowed() {
        if (this.autoplay && !this._paused && !this._videoHold && !this._animating) {
            this._startAutoplay();
        }
    }

    _bindVideosIn(container, store) {
        if (!container) return;
        this._unbindVideos(store);

        const videos = Array.from(container.querySelectorAll('video-media'));
        if (videos.length === 0 && this._playingVideosCount === 0) {
            // no videos in this container; make sure we don't hold autoplay
            this._videoHold = false;
        }

        videos.forEach(v => {
            const onPlay = () => {
                this._playingVideosCount++;
                this._pauseAutoplayForVideo();
            };
            const onPauseOrEnd = () => {
                this._playingVideosCount = Math.max(0, this._playingVideosCount - 1);
                if (this._playingVideosCount === 0) {
                    this._videoHold = false;
                    this._resumeAutoplayIfAllowed();
                }
            };
            v.addEventListener('play', onPlay);
            v.addEventListener('pause', onPauseOrEnd);
            v.addEventListener('ended', onPauseOrEnd);
            store.push({ el: v, play: onPlay, pause: onPauseOrEnd, ended: onPauseOrEnd });
        });
    }

    _unbindVideos(store) {
        if (!store || !store.length) return;
        store.forEach(({ el, play, pause, ended }) => {
            try {
                el.removeEventListener('play', play);
                el.removeEventListener('pause', pause);
                el.removeEventListener('ended', ended);
            } catch { }
        });
        store.length = 0;
    }

    _stopVideosIn(container) {
        if (!container) return;
        const vids = container.querySelectorAll('video-media');
        vids.forEach(v => {
            try { v.pause(); v.currentTime = 0; } catch { }
        });
        // reset counters/hold; we'll re-evaluate on the next active slide
        this._playingVideosCount = 0;
        this._videoHold = false;
    }

    // ========== PUBLIC API ==========
    next() { this.goTo((this._index + 1) % this._slides.length); }
    prev() { this.goTo((this._index - 1 + this._slides.length) % this._slides.length); }

    goTo(targetIndex, timeout) {
        if (this._animating || targetIndex === this._index) return;
        const current = this._slides[this._index];
        const next = this._slides[targetIndex];
        if (!current || !next) return;

        this._animating = true;
        this._setControlsDisabled(true);
        this._clearTimer?.(); // reset autoplay cadence on manual nav

        // Stop any playing videos on current slide and bg before navigating
        this._stopVideosIn(current);
        this._stopVideosIn(this._bgEl);

        const proceedForeground = () => {
            // Height lock & prep
            this._setTrackHeight(current.offsetHeight);
            current.classList.add('is-exiting');
            next.classList.add('is-entering');
            next.style.position = 'absolute';
            next.style.opacity = '0';

            // Reflow
            void this._track.offsetHeight;

            // To next height + fade
            this._setTrackHeight(next.offsetHeight);
            next.classList.add('is-active');
            next.style.opacity = '1';
            current.style.opacity = '0';

            const maxMs = (typeof timeout === 'number' && timeout >= 0)
                ? timeout
                : Math.max(this._fadeMs, this._heightMs);

            clearTimeout(this._doneT);
            this._doneT = setTimeout(done, maxMs + 50);
        };

        const done = () => {
            current.classList.remove('is-active', 'is-exiting');
            next.classList.remove('is-entering');

            // Only active slide should be relative for natural height
            this._slides.forEach((s, i) => {
                s.style.position = (i === targetIndex) ? 'relative' : 'absolute';
            });

            this._index = targetIndex;
            this._animating = false;

            this._setupResizeObserver();
            this._updateActiveDot?.();

            // Rebind video listeners for the new active slide
            this._bindVideosIn(next, this._boundSlideVideoHandlers);

            this._setControlsDisabled(false);
            this._resumeAutoplayIfAllowed(); // will restart if allowed
        };

        // If no bg target, just do the foreground transition
        if (!this._bgEl) {
            proceedForeground();
            return;
        }

        // Background sequence: fade-out → sync (await media) → rebind BG videos → fade-in → foreground
        this._fadeBg(0)
            .then(() => this._syncBgFromSlide(next))
            .then(() => { this._bindVideosIn(this._bgEl, this._boundBgVideoHandlers); })
            .then(() => this._fadeBg(1))
            .then(proceedForeground)
            .catch(proceedForeground);
    }

    // ========== HELPERS ==========
    _setTrackHeight(h, immediate = false) {
        if (immediate) {
            const prev = this._track.style.transition;
            this._track.style.transition = 'none';
            this._track.style.height = h + 'px';
            void this._track.offsetHeight;
            this._track.style.transition = prev || '';
        } else {
            this._track.style.height = h + 'px';
        }
    }

    _setupResizeObserver() {
        if (this._ro) this._ro.disconnect();
        const active = this._slides[this._index]; if (!active) return;
        this._ro = new ResizeObserver(() => this._setTrackHeight(active.offsetHeight));
        this._ro.observe(active);
        active.querySelectorAll('img').forEach(img => {
            if (!img.complete) img.addEventListener('load', () => this._setTrackHeight(active.offsetHeight), { once: true });
        });
    }

    _startAutoplay() {
        this._clearTimer();
        if (!this.autoplay || this._videoHold) return;
        this._timer = setTimeout(() => this.next(), this.interval);
    }
    _clearTimer() { if (this._timer) { clearTimeout(this._timer); this._timer = null; } }
    _pauseAutoplay = () => { this._paused = true; this._clearTimer(); };
    _resumeAutoplay = () => { this._paused = false; if (this.autoplay) this._startAutoplay(); };

    // --- BG cloning & fading ---
    _resolveBgTarget() {
        const sel = this.bgImagesSelector;
        this._bgEl = sel ? document.querySelector(sel) : null;
        if (this._bgEl) {
            // ensure initial visible state
            const cs = getComputedStyle(this._bgEl);
            if (!cs || cs.opacity === '') this._bgEl.style.opacity = '1';
        } else if (sel) {
            console.warn(`[impact-content-media] bg-images target not found: ${sel}`);
        }
    }

    _fadeBg(toOpacity, ms = this.bgFadeMs ?? this._bgFadeMs ?? 300) {
        if (!this._bgEl) return Promise.resolve();
        return new Promise(resolve => {
            const el = this._bgEl;
            const onEnd = (e) => {
                if (e.target === el && e.propertyName === 'opacity') {
                    el.removeEventListener('transitionend', onEnd);
                    clearTimeout(cap);
                    resolve();
                }
            };
            el.removeEventListener('transitionend', onEnd);
            el.addEventListener('transitionend', onEnd, { once: true });
            const cap = setTimeout(() => { el.removeEventListener('transitionend', onEnd); resolve(); }, ms + 60);

            el.style.transition = 'none';
            void el.offsetHeight; // reflow
            el.style.transition = `opacity ${ms}ms ease`;
            el.style.opacity = String(toOpacity);
        });
    }

    // Promise-based; clones .icm--slide-images into bg, attempts autoplay for <video-media>, waits for assets
    _syncBgFromSlide(slideEl) {
        if (!this._bgEl || !slideEl) return Promise.resolve();

        const source = slideEl.querySelector('.icm--slide-images');
        this._bgEl.innerHTML = '';
        if (!source) return Promise.resolve();

        const clones = Array.from(source.childNodes).map(n => n.cloneNode(true));
        this._bgEl.append(...clones);

        const imgs = Array.from(this._bgEl.querySelectorAll('img'));
        const videos = Array.from(this._bgEl.querySelectorAll('video-media'));

        // Attempt autoplay for bg videos (muted/inline/loop default)
        videos.forEach(video => {
            if (!video.hasAttribute('muted')) video.muted = true;
            if (!video.hasAttribute('playsinline')) video.setAttribute('playsinline', '');
            if (!video.hasAttribute('autoplay')) video.setAttribute('autoplay', '');
            if (!video.hasAttribute('loop')) video.setAttribute('loop', '');
            if (!video.hasAttribute('data-bg-controls')) video.removeAttribute('controls');
            try { video.currentTime = 0; } catch { }

            const tryPlay = () => {
                try { video.load?.(); } catch { }
                const p = video.play?.();
                if (p && typeof p.then === 'function') {
                    p.catch(() => {
                        const onData = () => {
                            video.removeEventListener('loadeddata', onData);
                            video.play?.().catch(() => { });
                        };
                        video.addEventListener('loadeddata', onData, { once: true });
                    });
                }
            };
            requestAnimationFrame(tryPlay);
        });

        const pending = [
            ...imgs.filter(img => !img.complete),
            ...videos.filter(v => (typeof v.readyState === 'number' ? v.readyState < 2 : false))
        ];

        if (pending.length === 0) return Promise.resolve();

        return new Promise(resolve => {
            let remaining = pending.length;
            const done = () => { if (--remaining <= 0) resolve(); };
            const cap = setTimeout(resolve, 800);
            const clearCapIfDone = () => { if (remaining <= 0) clearTimeout(cap); };

            pending.forEach(el => {
                el.addEventListener?.('load', () => { done(); clearCapIfDone(); }, { once: true });
                el.addEventListener?.('loadeddata', () => { done(); clearCapIfDone(); }, { once: true });
                el.addEventListener?.('error', () => { done(); clearCapIfDone(); }, { once: true });
            });
        });
    }

    // --- External controls ---
    _resolveControls() {
        const nextSel = this.getAttribute('next-btn');
        const prevSel = this.getAttribute('prev-btn');
        this._nextEl = nextSel ? document.querySelector(nextSel) : null;
        this._prevEl = prevSel ? document.querySelector(prevSel) : null;
        if (nextSel && !this._nextEl) console.warn(`[impact-content-media] next-btn target not found: ${nextSel}`);
        if (prevSel && !this._prevEl) console.warn(`[impact-content-media] prev-btn target not found: ${prevSel}`);
    }
    _bindControls() {
        if (this._nextEl) this._nextEl.addEventListener('click', this._onNextClick);
        if (this._prevEl) this._prevEl.addEventListener('click', this._onPrevClick);
    }
    _unbindControls() {
        if (this._nextEl) this._nextEl.removeEventListener('click', this._onNextClick);
        if (this._prevEl) this._prevEl.removeEventListener('click', this._onPrevClick);
    }
    _setControlsDisabled(disabled) {
        [this._nextEl, this._prevEl].forEach(el => {
            if (!el) return;
            if ('disabled' in el) el.disabled = disabled;
            el.setAttribute('aria-disabled', String(disabled));
            el.classList.toggle('is-disabled', disabled);
        });
    }

    // --- Dots ---
    _resolveDots() {
        const sel = this.getAttribute('dots-container');
        this._dotsWrap = sel ? document.querySelector(sel) : null;
        if (sel && !this._dotsWrap) console.warn(`[impact-content-media] dots-container target not found: ${sel}`);
        this._dots = this._dotsWrap ? Array.from(this._dotsWrap.querySelectorAll('[data-slide-index]')) : [];
        if (this._dotsWrap && !this._dotsWrap.hasAttribute('role')) this._dotsWrap.setAttribute('role', 'tablist');
        this._dots.forEach(dot => {
            if (!dot.hasAttribute('role')) dot.setAttribute('role', 'tab');
            dot.setAttribute('tabindex', dot.getAttribute('tabindex') ?? '0');
        });
    }
    _bindDots() {
        if (this._dotsWrap) {
            this._dotsWrap.addEventListener('click', this._onDotsClick);
            this._dotsWrap.addEventListener('keydown', this._onDotsKeydown = (e) => {
                const tgt = e.target.closest('[data-slide-index]');
                if (!tgt || !this._dotsWrap.contains(tgt)) return;
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tgt.click(); }
                if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                    e.preventDefault();
                    const arr = this._dots;
                    const cur = arr.indexOf(tgt);
                    if (cur === -1) return;
                    const nextIdx = e.key === 'ArrowRight' ? (cur + 1) % arr.length : (cur - 1 + arr.length) % arr.length;
                    arr[nextIdx].focus();
                }
            });
        }
    }
    _unbindDots() {
        if (!this._dotsWrap) return;
        this._dotsWrap.removeEventListener('click', this._onDotsClick);
        if (this._onDotsKeydown) this._dotsWrap.removeEventListener('keydown', this._onDotsKeydown);
    }
    _updateActiveDot() {
        if (!this._dotsWrap) return;
        this._dots.forEach(dot => {
            const i = parseInt(dot.getAttribute('data-slide-index') || '-1', 10);
            const isActive = i === this._index + 1; // 1-based in markup
            dot.classList.toggle(this._DOT_ACTIVE_CLASS, isActive);
            dot.setAttribute('aria-current', String(isActive));
            dot.setAttribute('current', String(isActive));
            dot.setAttribute('aria-selected', String(isActive));
        });
    }

    // --- Attr getters ---
    get autoplay() { const v = this.getAttribute('autoplay'); return v !== null && v !== 'false'; }
    get interval() { const v = parseInt(this.getAttribute('interval') || '6000', 10); return Number.isFinite(v) ? v : 6000; }
    get bgImagesSelector() { const v = this.getAttribute('bg-images'); return v && v.trim() ? v.trim() : null; }
    get initialSlide() { const v = parseInt(this.getAttribute('initial-slide') || '0', 10); return Number.isFinite(v) ? v : 0; }
}

// Define once
if (!window.customElements.get('impact-content-media')) {
    window.customElements.define('impact-content-media', ImpactContentMedia);
}

document.addEventListener('DOMContentLoaded', function () {
    // Example: after submit Shopify will redirect to /pages/contact#contact_form_id
    var hash = window.location.hash.replace('#', '');

    if (!hash) return; // no hash? nothing to show

    // Find the form with that ID
    var activeForm = document.getElementById(hash);

    if (!activeForm) return;

    // Only show the success message that belongs to THAT form
    var successEl = activeForm.querySelector('#' + hash + ' .form-submit-feedback');
    if (successEl) {
        successEl.style.display = 'block';
    }
});